<template>
<div class="order-edit">
	<!-- Form -->
	<form
		class="order-edit-form"
		@submit.prevent="save"
		v-if="order">
		<div class="container-fluid">
			<div class="row">
				<!-- Main -->
				<div class="col-md-8 p-0">
					<!-- Customer Data -->
					<div
						class="order-edit-section"
						:class="{ 'active': activeSection === 'customer' }">
						<div class="order-edit-section-title" @click="activeSection = 'customer'">
							<h5>
								<span>Customer Data</span>
								<i class="material-icons" v-if="activeSection === 'customer'">arrow_drop_up</i>
								<i class="material-icons" v-else>arrow_drop_down</i>
							</h5>
						</div>
						<!-- Customer Section -->
						<div
							class="order-edit-section-content"
							v-if="activeSection === 'customer'">
							<div class="row">
								<div class="col-sm-6">
									<!-- Name -->
									<div class="form-group mb-2">
										<label><b>Name <span class="pc-form-field-required">*</span></b></label>
										<input type="text" name="customer_name" class="form-control" v-model.trim="order.customer_data.name" required>
									</div>
								</div>
								<div class="col-sm-6">
									<!-- Phone -->
									<div class="form-group mb-2">
										<label><b>Phone <span class="pc-form-field-required">*</span></b></label>
										<input
											type="text"
											name="customer_phone"
											class="form-control"
											:disabled="!isOwner && !isUserCan('order_edit_phone')"
											v-model.trim="order.customer_data.phone"
											@change="onPhoneChange"
											required
										/>
									</div>
								</div>
								<div class="col-sm-6">
									<!-- Email -->
									<div class="form-group mb-2">
										<label><b>Email <span class="pc-form-field-required" v-if="!!orderData.customer_data.email">*</span></b></label>
										<input type="email" name="customer_email" class="form-control" v-model.trim="order.customer_data.email" :required="!!orderData.customer_data.email">
									</div>
								</div>
								<div class="col-sm-6">
									<!-- Address -->
									<div class="form-group mb-2">
										<label><b>Address <span class="pc-form-field-required" v-if="!!orderData.customer_data.address">*</span></b></label>
										<input type="text" name="customer_address" class="form-control" v-model.trim="order.customer_data.address" :required="!!orderData.customer_data.address">
									</div>
								</div>
							</div>
							<!-- Seller -->
							<div
								class="row mt-3"
								v-if="isDropship">
								<div class="col-sm-6">
									<div class="form-group">
										<label>Seller Name</label>
										<input
											type="text"
											class="form-control"
											v-model="orderData.dropship.name"
											readonly
										/>
									</div>
								</div>
								<div class="col-sm-6">
									<div class="form-group">
										<label>Seller Phone</label>
										<input
											type="text"
											class="form-control"
											v-model="orderData.dropship.phone"
											readonly
										/>
									</div>
								</div>
							</div>
							<div class="form-group mt-3" v-if="!isInstant">
								<!-- Show Address -->
								<label class="checkbox-wrapper">
									<input type="checkbox" class="input-checkbox" v-model="isShowAddress">
									<span></span>
									<span class="input-checkbox-text">Edit Address Details</span>
								</label>
							</div>
							<template v-if="isShowAddress && !isInstant">
								<!-- Address Details -->
								<div class="row">
									<div class="col-sm-6">
										<!-- Province/State -->
										<div class="form-group mb-2">
											<label><b>Province</b></label>
											<multiselect
												name="customer_province"
												placeholder="Customer Province"
												label="province_name"
												track-by="province_id"
												v-model="province"
												:options="provinces"
												:show-labels="false"
												:allow-empty="false"
												:loading="isFetchingProvince"
												:disabled="provinces.length === 0"
												@select="onProvinceChange">
											</multiselect>
										</div>
									</div>
									<div class="col-sm-6">
										<!-- City/District -->
										<div class="form-group mb-2">
											<label><b>City</b></label>
											<multiselect
												name="customer_city"
												placeholder="Customer City"
												label="city_name_with_type"
												track-by="city_id"
												v-model="city"
												:options="cities"
												:show-labels="false"
												:allow-empty="false"
												:loading="isFetchingCity"
												:disabled="cities.length === 0"
												@select="onCityChange">
											</multiselect>
										</div>
									</div>
									<div class="col-sm-6">
										<!-- Subdistrict -->
										<div class="form-group mb-2">
											<label><b>Subdistrict</b></label>
											<multiselect
												name="customer_subdistrict"
												placeholder="Customer Subdistrict"
												label="subdistrict_name"
												track-by="subdistrict_id"
												v-model="subdistrict"
												:options="subdistricts"
												:show-labels="false"
												:allow-empty="false"
												:loading="isFetchingSubdistrict"
												:disabled="subdistricts.length === 0"
												@select="onSubdistrictChange">
											</multiselect>
										</div>
									</div>
									<div class="col-sm-6">
										<!-- ZIP Code -->
										<div class="form-group mb-2">
											<label><b>ZIP Code</b></label>
											<input type="text" name="zip_code" class="form-control" v-model.trim="order.customer_data.zip">
										</div>
									</div>
								</div>
								<!-- Shipping -->
								<div
									class="row"
									v-if="isShowStoreShipping">
									<div class="col-sm-6">
										<!-- Courier -->
										<div class="form-group mb-2">
											<label><b>Courier</b></label>
											<multiselect
												placeholder="Shipping Courier"
												label="name"
												track-by="code"
												v-model="courier"
												:options="couriers"
												:show-labels="false"
												:allow-empty="false"
												:loading="isFetchingShipping"
												:disabled="isCourierDisabled"
												@select="onCourierChange">
											</multiselect>
										</div>
									</div>
									<div class="col-sm-6">
										<!-- Package -->
										<div class="form-group mb-2">
											<label><b>Package</b></label>
											<multiselect
												placeholder="Shipping Package"
												label="label"
												track-by="service"
												v-model="shippingPackage"
												:options="shippingPackages"
												:show-labels="false"
												:allow-empty="false"
												:loading="isFetchingShipping"
												:disabled="isCourierServiceDisabled"
												@select="onPackageChange">
											</multiselect>
											<small
												class="text-danger pl-2"
												v-if="!shippingPackage && !isCourierServiceDisabled && !isFetchingShipping">
												Anda belum memilih Service Kurir
											</small>
										</div>
									</div>
									<!-- Smart COD Info -->
									<div
										class="col-sm-12"
										v-if="isSmartCod">
										<div class="alert alert-info">
											{{ $t('Shipping Courier can\'t be edited when Smart Logistic is activated') }}
										</div>
									</div>
									<!-- Additional Shipping Cost -->
									<div class="col-sm-12">
										<div class="form-group mb-2">
											<label><b>Additional Shipping Cost</b></label>
											<input type="number" class="form-control" v-model.number="additionalShippingCost">
										</div>
									</div>
								</div>
							</template>

							<!-- Instant and From Form -->
							<template v-if="product && !isStorefront && isInstant">
								<!-- Custom Fields -->
								<div
									class="custom-fields mb-3"
									v-if="customFields.length > 0">
									<label><b>Custom Fields</b></label>
									<div class="well">
										<div
											class="custom-field form-group"
											v-for="customField in customFields"
											:key="customField.name">
											<label>{{ customField.label }}</label>
											<input
												class="form-control"
												:type="customField.type"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'input' && customField.type !== 'file'"
											/>
											<div
												class="flex-center"
												v-if="customField.field === 'input' && customField.type === 'file'">
												<a
													:href="customFieldsValues[customField.name]"
													target="_blank">
													File URL
												</a>
												<file-uploader
													class="ml-auto"
													btn-size="sm"
													:username="user.username"
													@uploaded="onCustomFieldUploaded(customField.name, ...arguments)"
												/>
											</div>
											<textarea
												rows="3"
												class="form-control"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'textarea'">
											</textarea>
											<select
												class="form-control"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'select'">
												<option
													v-for="(option, optionIndex) in customField.options"
													:key="optionIndex"
													:value="option">
													{{ option }}
												</option>
											</select>
										</div>
									</div>
								</div>

								<!-- Notes -->
								<div class="form-group mb-2">
									<label><b>Notes <span class="pc-form-field-required" v-if="!!orderData.customer_data.notes">*</span></b></label>
									<textarea
										name="order_notes"
										class="form-control"
										:value="order.customer_data.notes"
										:required="!!orderData.customer_data.notes"
										@input="onNotesChange">
									</textarea>
								</div>
							</template>
						</div>
					</div>

					<!-- Order Data -->
					<div
						class="order-edit-section"
						:class="{ 'active': activeSection === 'order' }"
						v-if="!isInstant">
						<!-- Title -->
						<div
							class="order-edit-section-title"
							@click="activeSection = 'order'">
							<h5>
								<span>Order Data</span>
								<i class="material-icons" v-if="activeSection === 'order'">arrow_drop_up</i>
								<i class="material-icons" v-else>arrow_drop_down</i>
							</h5>
						</div>
						<!-- Form Edit -->
						<div
							class="order-edit-section-content"
							v-if="activeSection === 'order'">
							<!-- From Form -->
							<template v-if="product && !isStorefront">
								<!-- Custom Fields -->
								<div
									class="custom-fields mb-3"
									v-if="customFields.length > 0">
									<label><b>Custom Fields</b></label>
									<div class="well">
										<div
											class="custom-field form-group"
											v-for="customField in customFields"
											:key="customField.name">
											<label>{{ customField.label }}</label>
											<input
												class="form-control"
												:type="customField.type"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'input' && customField.type !== 'file'"
											/>
											<div
												class="flex-center"
												v-if="customField.field === 'input' && customField.type === 'file'">
												<a
													:href="customFieldsValues[customField.name]"
													target="_blank">
													File URL
												</a>
												<file-uploader
													class="ml-auto"
													btn-size="sm"
													:username="user.username"
													@uploaded="onCustomFieldUploaded(customField.name, ...arguments)"
												/>
											</div>
											<textarea
												rows="3"
												class="form-control"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'textarea'">
											</textarea>
											<select
												class="form-control"
												v-model="customFieldsValues[customField.name]"
												v-if="customField.field === 'select'">
												<option
													v-for="(option, optionIndex) in customField.options"
													:key="optionIndex"
													:value="option">
													{{ option }}
												</option>
											</select>
										</div>
									</div>
								</div>

								<!-- Quantity -->
								<div
									class="form-group mb-2"
									v-if="!order.customer_data.variations">
									<label><b>Quantity</b></label>
									<input
										type="number"
										name="order_quantity"
										class="form-control"
										min="1"
										autocomplete="nope"
										:value="productQuantity"
										@input="onQuantityChange"
										@blur="onQuantityBlur">
								</div>

								<!-- Notes -->
								<div class="form-group mb-2">
									<label><b>Notes <span class="pc-form-field-required" v-if="!!orderData.customer_data.notes">*</span></b></label>
									<textarea
										name="order_notes"
										class="form-control"
										:value="order.customer_data.notes"
										:required="!!orderData.customer_data.notes"
										@input="onNotesChange">
									</textarea>
								</div>

								<!-- Multiple Variations -->
								<div
									class="form-group mb-2"
									v-if="isMultipleVariations">
									<label><b>Variations</b></label>
									<table class="table table-bordered table--small">
										<thead>
											<tr>
												<th width="70%">Variation</th>
												<th>Quantity</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(orderVariation, index) in orderVariations"
												:key="index">
												<td class="align-middle">
													{{ orderVariation.name.replace(/\|\|/g, ' - ') }}
												</td>
												<td>
													<input
														type="number"
														min="0"
														class="form-control"
														:value="orderVariation.quantity"
														@input="onOrderVariationInput(index, $event)">
												</td>
											</tr>
										</tbody>
									</table>
								</div>

								<!-- Product Attributes -->
								<div
									class="form-group mb-2"
									v-if="product.type === 'variable' && !isMultipleVariations">
									<label><b>Variations</b></label>
									<div class="row">
										<!-- Attributes -->
										<div
											class="col-md-6 mb-3"
											v-for="(attribute, index) in getVariationsAttributes()"
											:key="index">
											<label>{{ attribute.name }}</label>
											<multiselect
												v-model="orderVariation[index]"
												:placeholder="attribute.name"
												:options="attribute.values"
												:show-labels="false"
												:allow-empty="false"
												:id="index"
												@select="onSelectOrderVariation">
											</multiselect>
										</div>
									</div>
								</div>
							</template>

							<!-- From Storefront -->
							<template v-if="isStorefront">
								<div class="text-bold mb-2">Cart Items</div>
								<!-- Cart Items - Origin Store -->
								<div
									class="cart-items card mb-4"
									v-if="isShippingOriginStore">
									<cart-item
										v-for="(cartItem, index) in order.cart_items"
										:key="index"
										:products="order.products"
										:item="cartItem"
										:index="index"
										@change="onCartChange"
										@remove="onCartRemove(index)">
									</cart-item>
								</div>
								<!-- Cart Items - Origin Product -->
								<cart-groups
									:shipping-costs="order.shipping_costs"
									:products="order.products"
									:destination-id="destinationId"
									@change="onCartChange"
									@remove="removeCartItem"
									@change-shipping="onShippingChange"
									@remove-shipping="onRemoveShipping"
									v-model="cartItems"
									v-else>
								</cart-groups>
							</template>

							<div class="row mb-2">
								<!-- Payment Method -->
								<div class="col-sm-6">
									<div class="form-group mb-3">
										<label><b>Payment Method</b></label>
										<select
											name="payment_method"
											class="form-control"
											v-model="order.payment.method"
											@change="onPaymentChange">
											<option
												v-for="(paymentMethod, key) in paymentMethods"
												:key="key"
												:value="key">
												{{ paymentMethod }}
											</option>
										</select>
									</div>
								</div>
								<!-- Bump -->
								<div class="col-sm-6" v-if="!isStorefront">
									<div class="form-group mb-3" v-if="order.bump">
										<div><label><b>Bump</b></label></div>
										<label class="custom-control custom-checkbox d-inline-block">
											<input type="checkbox" class="custom-control-input" v-model="order.bump.checked" @change="onBumpChange">
											<span class="custom-control-label">{{ order.bump.name }} ({{ order.bump.weight }}gr) - {{ formatMoney(order.bump.price) }}</span>
										</label>
									</div>
								</div>
								<!-- Other Cost -->
								<div :class="{ 'col-sm-12': !isStorefront, 'col-sm-6': isStorefront }">
									<div class="form-group mb-2">
										<label><b>Other Cost</b></label>
										<input type="number" class="form-control" v-model.number="otherCost">
									</div>
								</div>
							</div>

							<!-- Receipt Number -->
							<div class="form-group mb-2">
								<label><b>Receipt Number</b></label>
								<input
									type="text"
									name="receipt_number"
									class="form-control"
									autocomplete="nope"
									v-model="order.shipping.receipt_number">
							</div>
						</div>
					</div>
				</div>

				<!-- Sidebar -->
				<div class="order-edit-sidebar col-md-4 p-0">
					<!-- Summary -->
					<div class="order-edit-section">
						<div class="order-edit-section-title">
							<h5>Order Summary</h5>
						</div>
						<div class="order-edit-section-content">
							<div class="order-edit-date d-flex justify-content-between align-items-center">
								<div>
									<div><b>{{ orderDate }}</b></div>
									<div><span>{{ orderTime }}</span></div>
								</div>
								<div>
									<i class="material-icons">date_range</i>
								</div>
							</div>
							<div class="box">
								<template v-if="isStorefront && order">
									<div
										class="d-flex justify-content-between"
										v-for="(cartItem, index) in order.cart_items"
										:key="index">
										<div>
											<label>{{ cartItem.product_name }} <small>(x{{ cartItem.qty }})</small></label>
										</div>
										<div class="text-right">{{ formatMoney(cartItem.price) }}</div>
									</div>
								</template>

								<template v-else>
									<!-- Product - Simple -->
									<div class="d-flex justify-content-between" v-if="isSimpleProduct">
										<div><label>{{ order.product_name }} <small>(x{{ productQuantity }})</small></label></div>
										<div class="text-right">{{ formatMoney(productPrice) }}</div>
									</div>

									<!-- Product - Variable -->
									<template v-if="!isSimpleProduct">
										<!-- Variable Simple -->
										<template v-if="!isMultipleVariations">
											<div class="d-flex justify-content-between">
												<div><label>{{ order.product_name }}</label></div>
											</div>
											<div class="d-flex justify-content-between">
												<div><label><small>{{ order.customer_data.variation }} (x{{ productQuantity }})</small></label></div>
												<div class="text-right">{{ formatMoney(productPrice) }}</div>
											</div>
										</template>
										<!-- Variable Multiple -->
										<template v-if="isMultipleVariations">
											<div class="d-flex justify-content-between">
												<div><label>{{ order.product_name }}</label></div>
											</div>
											<div class="d-flex justify-content-between" v-for="(orderVariation, index) in selectedOrderVariations" :key="index">
												<div><label><small>{{ orderVariation.value }} (x{{ orderVariation.quantity }})</small></label></div>
												<div class="text-right">{{ formatMoney(getOrderVariationPrice(orderVariation)) }}</div>
											</div>
										</template>
									</template>
								</template>

								<!-- Bump -->
								<div
									class="d-flex justify-content-between"
									v-if="order.bump && order.bump.checked">
									<div>
										<label>{{ order.bump.name }} <small>(Bump)</small></label>
									</div>
									<div class="text-right">{{ formatMoney(order.bump.price) }}</div>
								</div>

								<!-- Shipping -->
								<template v-if="isShowShipping || isCodIncludeShipping">
									<div class="d-flex justify-content-between">
										<div v-if="isStorefront">
											<label>Shipping <small>({{ totalCartWeight }}gr)</small></label>
										</div>
										<div v-else>
											<label>Shipping <small>({{ productWeight }}gr)</small></label>
										</div>
										<div class="text-right">{{ formatMoney(order.shipping.cost) }}</div>
									</div>
									<!-- Additional Shipping Cost -->
									<div class="d-flex justify-content-between" v-if="!isFreeShipping && !isFlatShipping && additionalShippingCost !== 0">
										<div>
											<label><small>Additional Shipping Cost</small></label>
										</div>
										<div class="text-right">{{ formatMoney(shippingMarkup) }}</div>
									</div>
								</template>

								<!-- Coupon -->
								<div
									class="d-flex justify-content-between"
									v-if="order.coupon && !isCouponEmpty && isCouponValid">
									<div>
										<label>Coupon <small>({{ order.coupon.code }})</small></label>
									</div>
									<div class="text-right">-{{ formatMoney(order.coupon.discount) }}</div>
								</div>

								<!-- COD Cost -->
								<div class="d-flex justify-content-between" v-if="isCod">
									<div>
										<label>COD <i class="info material-icons" v-tooltip="codInfo">info</i></label>
									</div>
									<div class="text-right">{{ formatMoney(codCost) }}</div>
								</div>

								<!-- Unique Code -->
								<div class="d-flex justify-content-between">
									<div><label>Unique Code</label></div>
									<div class="text-right">{{ uniqueCodeType }}{{ formatMoney(order.unique_code_price.unique_code) }}</div>
								</div>

								<!-- Other Cost -->
								<div class="d-flex justify-content-between" v-if="otherCost">
									<div><label>Other Cost</label></div>
									<div class="text-right">{{ formatMoney(otherCost) }}</div>
								</div>

								<!-- Total -->
								<div class="d-flex justify-content-between">
									<div class=""><b>Total</b></div>
									<div class="text-right"><b>{{ formatMoney(grossRevenue) }}</b></div>
								</div>
							</div>
						</div>
					</div>

					<!-- Coupon -->
					<div
						class="order-edit-section"
						v-if="!!order.coupon && !isInstant">
						<div class="order-edit-section-title">
							<h5>Coupon</h5>
						</div>
						<div class="order-edit-section-content pt-0">
							<div class="form-group">
								<div
									class="coupon-container"
									:class="{ 'coupon-valid': isCouponValidated && isCouponValid && !isCouponEmpty, 'coupon-invalid': isCouponValidated && !isCouponValid && !isCouponEmpty }">
									<!-- Coupon Code -->
									<input
										type="text"
										name="coupon_code"
										class="form-control coupon-field"
										v-model="order.coupon.code"
										:disabled="isCouponValid || isFetchingShipping"
										@keyup.enter="applyCoupon">
									<!-- Apply -->
									<button
										type="button"
										class="coupon-button coupon-apply-button"
										:class="{ 'active': !isCouponValidated && !isCouponEmpty && !isCouponValid }"
										@click="applyCoupon">Apply</button>
									<!-- Remove Coupon -->
									<button
										type="button"
										class="coupon-button coupon-remove-button"
										@click="removeCoupon">
										<i class="material-icons coupon-apply-button-remove-check">check</i>
										<i class="material-icons coupon-apply-button-remove-times">close</i>
									</button>
									<!-- Remove Failed Coupon -->
									<button
										type="button"
										class="coupon-button coupon-remove-button-failed"
										@click="removeCoupon">
										<i class="material-icons">close</i>
									</button>
								</div>
								<div class="coupon-error" v-if="!isCouponValid && couponError !== ''">{{ couponError }}</div>
							</div>
						</div>
					</div>

					<!-- COD Unavailable Error -->
					<div
						class="col-sm-12"
						v-if="isCodUnavailable">
						<div class="alert alert-danger">
							COD menggunakan {{ courierLabel }} ke "{{ subdistrict.subdistrict_name }}" belum tersedia
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="modal-footer">
			<button type="button" class="btn btn-light" @click="close">{{ $t('Close') }}</button>
			<button type="button" class="btn btn-light" @click="resetData">Reset</button>
			<button
				type="submit"
				class="btn btn-primary"
				:class="{ 'is-loading': isUpdating || isFetching }"
				:disabled="isEditInvalid">
				<i class="material-icons">save</i>
				<span>{{ $t('Update Order') }}</span>
			</button>
		</div>
	</form>

	<!-- Loader -->
	<div class="modal-loader" v-if="!order">
		<div class="is-loading large"></div>
	</div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FileUploader from '@/components/FileUploader.vue';
import CartItem from '@/components/CartItem.vue';
import CartGroups from '@/components/CartGroups.vue';
import {
	emptyCoupon,
	feeTypeLabels,
	paymentMethods,
	// shippingCouriers as allShippingCouriers,
} from '@/lib/statics';
import {
	duplicateVar,
	getAxiosErrorMessage,
	getWholesalePrice,
	formatMoney,
	getPropValue,
	arrayUnique,
	getCourierLabel,
} from '@/lib/helper';
import submissionsApi from '@/api/submissions';
import shippingApi from '@/api/shipping';
import productApi from '@/api/products';
import couponApi from '@/api/coupons';
import storefrontApi from '@/api/storefront';
import formsApi from '@/api/forms';

export default {
	name: 'OrderEdit',
	props: {
		store: {
			type: Object,
		},
		orderData: {
			type: Object,
		},
	},
	components: {
		CartItem,
		CartGroups,
		FileUploader,
	},
	data() {
		return {
			activeSection: 'customer',
			order: null,
			// orderDetails: null,
			province: null,
			city: null,
			subdistrict: null,
			isUpdating: false,
			courier: null,
			couriers: [],
			shippingPackage: null,
			shippingPackages: [],
			product: null,
			isFetchingOrder: true,
			isFetchingShipping: false,
			isCouponValid: false,
			isCouponValidated: false,
			isValidatingCoupon: false,
			couponError: '',
			productQuantity: 1,
			productWeight: 1000,
			shippingCostRequests: [],
			orderVariations: [],
			orderVariation: [],
			isShowAddress: false,
			paymentMethods,
			uniqueCode: 0,
			hasUniqueCode: false,
			formDetails: null,
			customFieldsValues: null,
		};
	},
	created() {
		this.initData();
	},
	methods: {
		...mapActions({
			fetchProvinces: 'productCreator/fetchProvinces',
			fetchCities: 'productCreator/fetchCities',
			fetchSubdistricts: 'productCreator/fetchSubdistricts',
		}),
		close() {
			this.$emit('close');
		},
		initData() {
			// Coupon
			if (this.orderData.coupon && this.orderData.coupon.code !== '') {
				this.isCouponValid = true;
				this.isCouponValidated = true;
			}
			const isBankTransfer = this.orderData.payment && this.orderData.payment.method === 'bank_transfer';
			// Unique Code
			const uniqueCode = this.orderData.unique_code_price && this.orderData.unique_code_price.unique_code && this.orderData.unique_code_price.unique_code !== ''
				? duplicateVar(this.orderData.unique_code_price.unique_code) : 0;
			this.uniqueCode = uniqueCode;
			// Has Unique Code
			const hasUniqueCode = isBankTransfer && uniqueCode !== 0;
			this.hasUniqueCode = hasUniqueCode;
			// Order Details
			this.getOrderDetails();
			// Product Details
			if (this.orderData.product_id) {
				this.getProductDetails(this.orderData.product_id);
			}
		},
		initCustomFields() {
			// Get Form Details
			if (this.orderData.customer_data.meta) {
				const metas = this.orderData.customer_data.meta;
				const values = {};
				metas.forEach(({ name, value }) => {
					values[name] = value;
				});
				this.customFieldsValues = duplicateVar(values);
				const formId = this.order.form_id;
				this.getFormDetails(formId);
			}
		},
		getFormDetails(formId) {
			const callback = (response) => {
				const formDetails = response.data;
				this.formDetails = formDetails;
			};
			const errorCallback = () => {};
			formsApi.getDetails(formId, callback, errorCallback);
		},
		initShippingData() {
			// Province
			const isProvincesEmpty = this.provinces.length === 0;
			if (isProvincesEmpty) {
				this.fetchProvinces();
			} else {
				this.setProvince();
			}
			// Set Shipping Cost to 0 if COD not include shipping
			const shipping = this.order.shipping;
			const isStorefront = this.order.type === 'storefront';
			if (!isStorefront && shipping.cod && !shipping.cod_include_shipping) {
				this.order.shipping.cost = 0;
			}
		},
		getParams() {
			const params = {};
			// Customer Data
			const customerData = duplicateVar(this.order.customer_data);
			if (customerData.variation) {
				customerData.variation_name = this.getVariationName();
			}

			if (customerData.variations) {
				customerData.variations = this.getSelectedOrderVariations();
			}

			if (customerData.meta) {
				const customerDataMeta = this.customFields.map((field) => {
					const value = this.customFieldsValues[field.name];
					const meta = {
						label: field.label,
						name: field.name,
						value,
					};
					return meta;
				});
				customerData.meta = customerDataMeta;
			}

			params.customer_data = JSON.stringify(customerData);

			// Shipping
			const shipping = this.order.shipping;
			shipping.cost = this.shippingCost;
			shipping.original_cost = this.shippingCostOriginal;

			if (!shipping.markup) {
				shipping.markup = this.additionalShippingCost;
			}

			// Get SLA Min and Max
			const shippingPackage = this.shippingPackage;
			const firstPackage = shippingPackage && shippingPackage.cost && shippingPackage.cost[0] ? shippingPackage.cost[0] : null;

			let slaString = firstPackage && firstPackage.etd ? firstPackage.etd : '';
			slaString = slaString.replace('hari', '');
			slaString = slaString.trim();

			const slaSplitted = slaString.split('-');
			const slaMin = slaSplitted[0] ? parseInt(slaSplitted[0].trim()) : null;
			const slaMax = slaSplitted[1] ? parseInt(slaSplitted[1].trim()) : slaMin;

			shipping.sla_min = slaMin;
			shipping.sla_max = slaMax;

			// Remove COD Fee if bank transfer
			if (this.order.payment && this.order.payment.method === 'bank_transfer') {
				// shipping.cod_fee = 0;
				shipping.cod_cost = 0;
			}

			// Add Weight to Shipping Data
			shipping.weight = this.totalWeight;

			const shippingData = JSON.stringify(shipping);
			const oldShippingData = JSON.stringify(this.orderData.shipping);
			if (shippingData !== oldShippingData) {
				params.shipping = shippingData;
			}
			params.shipping = shippingData;

			// Coupon
			if (this.order.coupon) {
				const isCouponIdObject = typeof this.order.coupon.id === 'object';
				if (isCouponIdObject) {
					const couponId = this.order.coupon.id.$oid;
					this.order.coupon.id = couponId;
				}
				params.coupon = JSON.stringify(this.order.coupon);
			}

			// Bump
			params.bump = JSON.stringify(this.order.bump);

			// Unique Code
			const uniqueCodePrice = this.order.unique_code_price;
			if (uniqueCodePrice.type === '') {
				uniqueCodePrice.type = this.product && this.product.unique_code_price
					? this.product.unique_code_price.type : 'decrease';
			}
			params.unique_code_price = JSON.stringify(uniqueCodePrice);

			// Payment
			params.payment = JSON.stringify(this.order.payment);

			// Other Cost
			params.other_cost = this.otherCost;

			return params;
		},
		getStorefrontParams() {
			// Customer Data
			const customerData = duplicateVar(this.order.customer_data);

			// Shipping
			const shipping = this.order.shipping;
			shipping.cost = this.shippingCost;

			if (!shipping.markup) {
				shipping.markup = this.additionalShippingCost;
			}

			// Remove COD Fee if bank transfer
			if (this.order.payment && this.order.payment.method === 'bank_transfer') {
				// shipping.cod_fee = 0;
				shipping.cod_cost = 0;
			}

			const shippingData = JSON.stringify(shipping);

			// Unique Code
			const uniqueCodePrice = this.order.unique_code_price;
			if (uniqueCodePrice.type === '') {
				uniqueCodePrice.type = this.product && this.product.unique_code_price
					? this.product.unique_code_price.type : 'decrease';
			}
			const uniqueCodeData = JSON.stringify(uniqueCodePrice);

			// Payment
			const paymentData = JSON.stringify(this.order.payment);

			// Other Cost
			const otherCostData = this.otherCost;

			const cartItems = this.order.cart_items;
			const cartItemsData = JSON.stringify(cartItems);

			const params = {};
			params.cart_items = cartItemsData;
			params.customer_data = JSON.stringify(customerData);
			params.shipping = shippingData;
			params.unique_code_price = uniqueCodeData;
			params.payment = paymentData;
			params.other_cost = otherCostData;

			// Coupon
			if (this.order.coupon) {
				const isCouponIdObject = typeof this.order.coupon.id === 'object';
				if (isCouponIdObject) {
					const couponId = this.order.coupon.id.$oid;
					this.order.coupon.id = couponId;
				}
				params.coupon = JSON.stringify(this.order.coupon);
			}

			// Shipping Costs - Storefronts
			if (this.order.shipping_costs) {
				const shippingCosts = duplicateVar(this.order.shipping_costs);
				if (shippingCosts.length > 0 && this.isShippingOriginProduct) {
					const singleShippingCost = shippingCosts[0];
					const newShipping = Object.assign({}, shipping);
					newShipping.courier = singleShippingCost.courier;
					newShipping.service = singleShippingCost.service;
					newShipping.cost = singleShippingCost.cost;
					const newShippingData = JSON.stringify(newShipping);
					params.shipping = newShippingData;
				}
				params.shipping_costs = JSON.stringify(shippingCosts);
			}

			return params;
		},
		save() {
			this.isUpdating = true;
			const params = this.isStorefront ? this.getStorefrontParams() : this.getParams();
			const orderId = this.order._id;
			const callback = (response) => {
				const order = response.data;
				this.isUpdating = false;
				this.$emit('update', order);
				this.close();
			};
			const errorCallback = (error) => {
				const errorMessage = getAxiosErrorMessage(error);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Update Order',
					text: errorMessage,
				});
				this.isUpdating = false;
			};
			if (!this.isInstant) {
				submissionsApi.update(orderId, params, callback, errorCallback);
			} else {
				const customerParams = this.customerParams;
				const customerCallback = (response) => {
					const order = response.data;
					this.isUpdating = false;
					this.$emit('updateCustomer', order);
					this.close();
				};
				submissionsApi.updateCustomer(orderId, customerParams, customerCallback, errorCallback);
			}
		},
		setProvince() {
			// const provinceName = this.order.customer_data.state;
			const provinceName = this.order ? this.order.customer_data.state : this.orderData.customer_data.state;
			const provinceId = this.order.customer_data.province_id;
			const province = this.provinces.find(province => province.province_name === provinceName || province.province_id === provinceId);
			if (province) {
				this.province = province;
			}
		},
		setCity() {
			const cityName = this.order.customer_data.city;
			const cityId = this.order.customer_data.city_id;
			const city = this.cities.find(city => city.city_name_with_type === cityName || city.city_id === cityId);
			this.city = city;
		},
		setSubdistrict() {
			const subdistrictName = this.order.customer_data.district;
			const subdistrictId = this.order.customer_data.subdistrict_id;
			const subdistrict = this.subdistricts.find(subdistrict => subdistrict.subdistrict_name === subdistrictName || subdistrict.subdistrict_id === subdistrictId);
			if (subdistrict) {
				this.subdistrict = subdistrict;
			} else {
				this.subdistrict = null;
			}
		},
		setCourier() {
			const shipping = this.order.shipping;
			let courier = this.couriers.find(courier => courier.code === shipping.courier);
			if (courier) {
				this.courier = courier;
			} else if (this.isSmartCod && this.couriers.length > 0) {
				// Get First Courier
				courier = duplicateVar(this.couriers[0]);
				this.courier = courier;

				// If current order courier is empty, insert the smart courier
				if (this.order && this.order.shipping) {
					this.order.shipping.courier = courier.code;
				}
			} else {
				this.courier = null;
			}
		},
		setShippingPackage() {
			const shipping = this.order.shipping;
			let shippingService = shipping.service;
			if (shippingService === 'CTC') {
				shippingService = 'REG';
			} else if (shippingService === 'CTCYES') {
				shippingService = 'YES';
			}
			const shippingPackage = this.shippingPackages.find(shippingPackage => shippingPackage.service === shippingService);
			if (shippingPackage) {
				this.shippingPackage = shippingPackage;
			} else if (this.isSmartCod && this.shippingPackages.length > 0) {
				// Get First Package
				let smartService = 'REG';
				const currentCourierCode = this?.courier?.code ?? '';
				if (currentCourierCode === 'sicepat' || currentCourierCode === 'jne') {
					smartService = 'REG';
				} else if (currentCourierCode === 'sap') {
					smartService = 'UDRREG';
				} else if (currentCourierCode === 'jnt' || currentCourierCode === 'J&T') {
					smartService = 'EZ';
				}
				const smartPackage = this.shippingPackages.find(shippingPackage => shippingPackage.service === smartService);
				this.shippingPackage = smartPackage;
			} else if (this.shippingPackages.length > 0) {
				// Get Regular Package
				let regularService = 'REG';
				const currentCourierCode = this?.courier?.code ?? '';
				if (currentCourierCode === 'sicepat' || currentCourierCode === 'jne') {
					regularService = 'REG';
				} else if (currentCourierCode === 'sap') {
					regularService = 'UDRREG';
				} else if (currentCourierCode === 'jnt' || currentCourierCode === 'J&T') {
					regularService = 'EZ';
				}
				const regularPackage = this.shippingPackages.find(shippingPackage => shippingPackage.service === regularService);
				if (regularPackage) {
					this.shippingPackage = regularPackage;
				} else {
					// Choose First Package
					const firstPackage = this.shippingPackages[0];
					this.shippingPackage = firstPackage;
				}
			} else {
				this.shippingPackage = null;
			}
		},
		onProvinceChange(province) {
			this.city = null;
			this.subdistrict = null;
			this.courier = null;
			this.shippingPackage = null;
			this.couriers = [];
			this.shippingPackages = [];
			const provinceId = province.province_id;
			const provinceName = province.province_name;
			this.order.customer_data.state = provinceName;
			this.order.customer_data.province_id = provinceId;
			this.order.customer_data.province_name = provinceName;
		},
		onCityChange(city) {
			this.subdistrict = null;
			this.courier = null;
			this.shippingPackage = null;
			this.couriers = [];
			this.shippingPackages = [];
			const cityId = city.city_id;
			const cityName = city.city_name_with_type;
			this.order.customer_data.city = cityName;
			this.order.customer_data.city_id = cityId;
			this.order.customer_data.city_name = cityName;
		},
		onSubdistrictChange(subdistrict) {
			this.resetShipping();
			const subdistrictId = subdistrict.subdistrict_id;
			const subdistrictName = subdistrict.subdistrict_name;
			this.order.customer_data.district = subdistrictName;
			this.order.customer_data.subdistrict_id = subdistrictId;
			this.order.customer_data.subdistrict_name = subdistrictName;
		},
		onCourierChange(courier) {
			this.shippingPackage = null;
			this.shippingPackages = [];
			this.order.shipping.courier = courier.code;
		},
		/**
		 * On Package Change
		 * @deprecated since version 1.4.1
		 */
		onPackageChange() {
			// const oldShippingCost = duplicateVar(this.order.shipping.cost);
			// const cost = shippingPackage && shippingPackage.cost[0] ? shippingPackage.cost[0].value : 0;
			// this.order.shipping.service = shippingPackage.service;
			// this.order.shipping.cost = cost;
			// if (cost !== oldShippingCost) {
			// 	const oldGrossRevenue = duplicateVar(this.order.gross_revenue);
			// 	const grossRevenue = (oldGrossRevenue - oldShippingCost) + cost;
			// 	this.order.gross_revenue = grossRevenue;

			// 	// Re-validate Coupon
			// 	if (this.order.coupon.code !== '') {
			// 		this.applyCoupon();
			// 	}
			// }
		},
		// Get Shipping Cost
		getShippingCosts() {
			const isStorefront = this.orderData.type === 'storefront';
			const isCodAndExcludeShipping = this.orderData.shipping.cod && !this.orderData.shipping.cod_include_shipping && !isStorefront;
			const isNoShippingCost = !this.subdistrict || (!this.product && !this.isStorefront) || isCodAndExcludeShipping;
			if (isNoShippingCost) {
				return;
			}
			this.isFetchingShipping = true;
			const timestamp = +new Date();
			this.shippingCostRequests.push(timestamp);
			this.couriers = [];
			this.shippingPackages = [];
			const storeCouriers = this.store.shipping.couriers || [];
			const storeShippingOrigin = this.store.shipping.origin;
			const provinceId = storeShippingOrigin ? storeShippingOrigin.province.id : 0;
			const cityId = storeShippingOrigin ? storeShippingOrigin.city.id : 0;
			const cityName = storeShippingOrigin ? storeShippingOrigin.city.name : '';
			let subdistrictId = storeShippingOrigin && storeShippingOrigin.subdistrict ? storeShippingOrigin.subdistrict.id : 0;
			let subdistrictName = storeShippingOrigin && storeShippingOrigin.subdistrict ? storeShippingOrigin.subdistrict.name : '';
			const productOrigin = !this.isStorefront && this.product ? this.product.shipping.origin : null;
			const originProvince = productOrigin ? productOrigin.province_id : provinceId;
			let originName = productOrigin ? productOrigin.name : cityName;
			let originType = productOrigin && productOrigin.type !== '' ? productOrigin.type : 'city';
			let originId = cityId;
			if (productOrigin && productOrigin.id !== 0) {
				originId = productOrigin.id;
				if (productOrigin.subdistrict_id && productOrigin.subdistrict_id !== '') {
					subdistrictId = productOrigin.subdistrict_id;
					subdistrictName = productOrigin.subdistrict_name;
				}
			} else if (productOrigin && productOrigin.id === 0 && productOrigin.city_id) {
				originId = productOrigin.city_id;
			}
			if (subdistrictId && subdistrictId !== 0) {
				originType = 'subdistrict';
				originId = subdistrictId;
				originName = subdistrictName;
			}
			let shippingCouriers = this.product && this.product.shipping.couriers ? this.product.shipping.couriers : [];
			const codShippingCouriers = this.product && this.product.shipping.cod_couriers ? this.product.shipping.cod_couriers : [];
			shippingCouriers = arrayUnique(shippingCouriers.concat(codShippingCouriers));

			if (this.isStorefront) {
				shippingCouriers = storeCouriers;
			}

			const destinationSubdistrictId = this.subdistrict.subdistrict_id;
			const totalWeight = this.totalWeight;
			const shippingOrigin = {
				type: originType,
				id: originId,
				name: originName,
				province_id: originProvince,
			};
			const shippingDestination = {
				id: destinationSubdistrictId,
				type: 'subdistrict',
			};
			const shippingProduct = { weight: totalWeight };
			const origin = JSON.stringify(shippingOrigin);
			const couriers = JSON.stringify(shippingCouriers);
			const destination = JSON.stringify(shippingDestination);
			const product = JSON.stringify(shippingProduct);
			const mode = this.shippingMode;
			const params = {
				origin,
				destination,
				couriers,
				product,
				// mode,
			};

			// If Subdistrict Changed, add mode
			// eslint-disable-next-line camelcase
			let originalSubdistrictId = this?.orderData?.customer_data?.subdistrict_id ?? 0;
			originalSubdistrictId = parseInt(originalSubdistrictId);
			const isSubdistrictChanged = originalSubdistrictId !== destinationSubdistrictId;

			// console.log('subdistrict', originalSubdistrictId, destinationSubdistrictId);
			// console.log('isSubdistrictChanged', isSubdistrictChanged);
			// console.log('isSmartCod', this.isSmartCod);

			if (isSubdistrictChanged) {
				params.mode = mode;
			} else if (this.isSmartCod) {
				// const allCouriers = allShippingCouriers.map(({ key }) => key);
				const allCouriers = ['sap', 'sicepat', 'jne', 'jnt'];
				const allCouriersJson = JSON.stringify(allCouriers);
				params.couriers = allCouriersJson;
			}

			// const couriers = JSON.stringify(shippingCouriers);

			// const params = {
			// 	origin_id: originId,
			// 	destination_id: destinationSubdistrictId,
			// 	couriers,
			// 	weight: totalWeight,
			// 	fallback: 'true', // Request to each couriers if not exist on our database
			// };

			const callback = (response) => {
				this.isFetchingShipping = false;
				const index = this.shippingCostRequests.indexOf(timestamp);
				this.shippingCostRequests.splice(index, 1);
				const couriers = response.data;
				this.couriers = couriers;
			};

			const errorCallback = () => {
				this.isFetchingShipping = false;
				const index = this.shippingCostRequests.indexOf(timestamp);
				this.shippingCostRequests.splice(index, 1);
			};

			shippingApi.getCosts(params, callback, errorCallback);
			// shippingApi.getShippingCosts(params, callback, errorCallback);
		},
		getOrderDetails() {
			const orderId = this.orderData._id;
			const callback = (response) => {
				const order = response.data;
				this.setOrder(order);
			};
			submissionsApi.getDetails(orderId, callback);
		},
		setOrder(order) {
			// Quantity
			if (!order.customer_data.quantity) {
				order.customer_data.quantity = 1;
			}
			// Notes
			if (!order.customer_data.notes) {
				order.customer_data.notes = '';
			}
			// Order Details
			this.order = duplicateVar(order);
			this.initCustomFields();
			// Set Quantity
			this.setQuantity();
			// Multiple Variations
			if (this.isMultipleVariations) {
				this.setOrderVariations();
			} else if (this.orderData.variation) {
				this.setOrderVariation();
			}
			// Coupon
			if (this.isCouponEmpty) {
				this.isCouponValid = false;
			} else {
				this.isCouponValidated = true;
			}
			// Address
			this.isShowAddress = duplicateVar(this.showAddressDetails);
			// Shipping Data
			// if (this.showAddressDetails) {
			if (this.showAddressDetails && !this.isInstant) {
				this.initShippingData();
			}
			this.setWeight();
		},
		getProductDetails(productId) {
			// const productId = this.orderData.product_id;
			const callback = (response) => {
				const product = response.data;
				if (product) {
					this.product = product;
				}
				// Weight
				this.setWeight();
			};
			const errorCallback = () => {};
			productApi.getProductDetails(productId, callback, errorCallback);
		},
		onNotesChange(event) {
			const notes = event.target.value;
			this.order.customer_data.notes = notes;
		},
		onQuantityChange(event) {
			const value = event.target.value;
			const oldQuantity = duplicateVar(this.order.customer_data.quantity);
			if (value !== '') {
				const parsedQuantity = parseInt(value);
				this.order.customer_data.quantity = parsedQuantity;
				this.setQuantity(parsedQuantity);
				this.setWeight();
				this.calculateProductPrice();
				this.calculateCodCost();
			}
			const isQuantityDiff = oldQuantity !== this.order.customer_data.quantity;
			if (isQuantityDiff) {
				this.resetShipping();
				this.getShippingCosts();
			}
		},
		onQuantityBlur(event) {
			const value = event.target.value;
			if (value === '') {
				this.order.customer_data.quantity = 1;
				this.setQuantity(1);
				event.target.value = 1;
			}
		},
		onCartChange() {
			this.calculateCodCost();
			this.calculateProductPrice();
			if (this.isShowStoreShipping) {
				this.resetShipping();
				this.getShippingCosts();
			}
		},
		onCartRemove(index) {
			const items = this.order.cart_items ? duplicateVar(this.order.cart_items) : [];
			if (items.length > 0 && items[index]) {
				items.splice(index, 1);
				this.$set(this.order, 'cart_items', items);
			}
			this.onCartChange();
		},
		getVariationName() {
			const orderVariation = this.order.customer_data.variation;
			const orderVariationSplit = orderVariation.split(', ');
			const mapper = variation => variation.split(': ')[1];
			const orderVariationValues = orderVariationSplit.map(mapper);
			const orderVariationName = orderVariationValues.join('||');
			return orderVariationName;
		},
		getQuantity() {
			const defaultQuantity = 1;
			const customerData = this.order.customer_data;
			let quantity = customerData.quantity ? customerData.quantity : defaultQuantity;
			const isMultipleVariations = !!customerData.variations;
			if (this.isStorefront) {
				const cartItems = this.order && this.order.cart_items ? this.order.cart_items : [];
				quantity = cartItems.reduce((prevValue, cartItem) => prevValue + cartItem.qty, 0);
			} else if (isMultipleVariations) {
				const orderVariations = customerData.variations;
				const reducer = (accumulator, orderVariation) => {
					const quantity = orderVariation.quantity;
					return accumulator + quantity;
				};
				const variationsQuantity = orderVariations.reduce(reducer, 0);
				quantity = variationsQuantity;
			}
			return quantity;
		},
		setQuantity(quantity) {
			const productQuantity = quantity || this.getQuantity();
			this.productQuantity = productQuantity;
		},
		getWeight() {
			const defaultWeight = 1000;
			const isSimple = this.product.type === 'simple';
			// const quantity = this.order.customer_data.quantity ? this.order.customer_data.quantity : 1;
			const quantity = this.productQuantity;
			const productWeight = this.simpleProductWeight;
			const simpleProductWeight = productWeight * quantity;
			let weight = isSimple ? simpleProductWeight : defaultWeight;
			if (!isSimple) {
				const isMultipleVariations = !!this.order.customer_data.variations;
				const variationsPrices = this.getVariationsPrices();
				if (isMultipleVariations) {
					// const orderVariations = this.order.customer_data.variations;
					const orderVariations = this.orderVariations;
					const reducer = (accumulator, orderVariation) => {
						let variationWeight = orderVariation.weight;
						if (!variationWeight) {
							const variation = variationsPrices.find(variation => variation.name === orderVariation.name);
							variationWeight = variation.weight;
						}
						const weight = variationWeight * orderVariation.quantity;
						return accumulator + weight;
					};
					const variationsWeight = orderVariations.reduce(reducer, 0);
					weight = variationsWeight;
				} else {
					const orderVariationName = this.getVariationName();
					const variationPrice = variationsPrices.find(price => price.name === orderVariationName);
					if (variationPrice) {
						const variationWeight = variationPrice.weight ? variationPrice.weight : productWeight;
						weight = variationWeight * quantity;
					}
				}
			}

			// Add Bump Weight
			if (this.order.bump && this.order.bump.checked) {
				const bumpWeight = this.order.bump.weight;
				weight += bumpWeight;
			}

			return weight;
		},
		setWeight(weight) {
			const productWeight = weight || this.getWeight();
			this.productWeight = productWeight;
		},
		resetShipping() {
			this.courier = null;
			this.shippingPackage = null;
			this.couriers = [];
			this.shippingPackages = [];
		},
		resetData() {
			this.initData();
		},
		setCoupon(coupon) {
			if (coupon.discount_for === 'product' && coupon.discount > this.productPrice) {
				coupon.discount = parseInt(this.productPrice);
			} else if (coupon.discount_for === 'shipping' && coupon.discount > this.shippingCost) {
				coupon.discount = parseInt(this.shippingCost);
			}
			this.$set(this.order, 'coupon', coupon);
			const grossRevenue = this.order.gross_revenue - coupon.discount;
			this.order.gross_revenue = grossRevenue;

			// Calculate COD
			this.calculateCodCost();
		},
		applyCoupon() {
			this.isValidatingCoupon = true;
			const couponCode = this.order.coupon.code.toUpperCase();
			// const shippingCost = this.order.shipping.cost + this.additionalShippingCost;
			const shippingCost = this.shippingCost;
			// const quantity = this.productQuantity;
			const quantity = this.getQuantity();
			const variations = this.getVariations();
			const variation = !variations && this.order.customer_data.variation
				? this.getVariationName() : null;

			const paymentMethod = this.order.payment.method;

			if (couponCode !== '') {
				const params = {
					coupon_code: couponCode,
					shipping_cost: shippingCost,
					quantity,
					ignore_quota: true,
					payment_method: paymentMethod,
				};

				if (this.isStorefront) {
					let cartItems = this.order && this.order.cart_items
						? duplicateVar(this.order.cart_items) : [];
					cartItems = cartItems.map((cartItem) => {
						const slimCartItem = duplicateVar(cartItem);
						slimCartItem.product_id = slimCartItem.product_id.$oid;
						delete slimCartItem.notes;
						delete slimCartItem.origin;
						return slimCartItem;
					});
					const cartItemsData = JSON.stringify(cartItems);
					params.cart_items = cartItemsData;
				} else {
					// From Form
					const productId = this.product._id;
					params.product_id = productId;
					if (variation) {
						params.variation = variation;
					}
					if (variations && variations.length > 0) {
						params.variations = variations;
					}
				}

				const callback = (response) => {
					this.isValidatingCoupon = false;
					this.isCouponValid = true;
					this.isCouponValidated = true;
					this.couponError = '';
					const coupon = response.data;
					this.setCoupon(coupon);
				};

				const errorCallback = (error) => {
					this.isValidatingCoupon = false;
					this.isCouponValid = false;
					this.isCouponValidated = true;
					const errorMessage = getAxiosErrorMessage(error);
					this.couponError = errorMessage;
				};

				if (this.isStorefront) {
					const username = this.user.username;
					couponApi.validateStorefront(username, params, callback, errorCallback);
				} else {
					couponApi.validate(params, callback, errorCallback);
				}
			} else {
				this.isValidatingCoupon = false;
			}
		},
		removeCoupon() {
			const coupon = duplicateVar(this.order.coupon);
			this.order.coupon.discount = 0;
			this.isCouponValid = false;
			this.isCouponValidated = false;
			this.$set(this.order, 'coupon', emptyCoupon);
			const grossRevenue = this.order.gross_revenue + coupon.discount;
			this.order.gross_revenue = grossRevenue;
			this.couponError = '';
		},
		removeCouponCode() {
			this.isCouponValidated = false;
			this.order.coupon.code = '';
			this.order.coupon.discount = 0;
			this.couponError = '';
		},
		getProductPrice() {
			let productPrice = 0;
			if (this.isStorefront) {
				productPrice = this.totalCartPrice;
			} else if (!this.isSimpleProduct && !this.isMultipleVariations) {
				productPrice = this.getVariationTotalPrice();
			} else if (!this.isSimpleProduct && this.isMultipleVariations) {
				productPrice = this.getOrderVariationsTotalPrice();
			} else {
				productPrice = this.simpleProductPrice * this.productQuantity;
			}
			return productPrice;
		},
		calculateProductPrice() {
			const productPrice = this.getProductPrice();
			this.order.product_price = productPrice;
		},
		getVariationPrice() {
			const orderVariationName = this.getVariationName();
			const variationsPrices = this.getVariationsPrices();
			const variationPrice = variationsPrices.find(price => price.name === orderVariationName);
			return variationPrice;
		},
		getVariationTotalPrice() {
			let price = this.simpleProductPrice;
			const quantity = this.productQuantity;
			const variationPrice = this.getVariationPrice();
			if (variationPrice) {
				price = parseInt(variationPrice.price);

				// Check for Wholesale Price
				if (quantity > 1 && variationPrice.wholesale) {
					const wholesales = variationPrice.wholesale;
					const wholesalePrice = getWholesalePrice(wholesales, quantity, price);
					price = wholesalePrice;
				}
			}
			const totalPrice = price * quantity;
			return totalPrice;
		},
		getVariations() {
			const isHaveVariations = !!this.order.customer_data.variations;
			const selectedVariations = isHaveVariations ? JSON.stringify(this.order.customer_data.variations) : null;
			return selectedVariations;
		},
		getVariationsAttributes() {
			let variationsAttributes = [];
			if (this.order.product_data && this.order.product_data.variations) {
				variationsAttributes = this.order.product_data.variations.attributes;
			} else {
				variationsAttributes = this.product && this.product.variations ? this.product.variations.attributes : [];
			}
			return variationsAttributes;
		},
		getVariationsPrices() {
			let variationsPrices = [];
			if (this.order.product_data && this.order.product_data.variations) {
				variationsPrices = this.order.product_data.variations.prices;
			} else {
				variationsPrices = this.product.variations ? this.product.variations.prices : [];
			}
			return variationsPrices;
		},
		getOrderVariationPrice(orderVariation) {
			const quantity = orderVariation.quantity;
			let price = duplicateVar(orderVariation.price);
			if (orderVariation.wholesale) {
				const wholesalePrice = getWholesalePrice(orderVariation.wholesale, quantity, price);
				price = wholesalePrice;
			} else {
				const variationsPrices = this.getVariationsPrices();
				const variationPrice = variationsPrices.find(price => price.name === orderVariation.name);
				if (variationPrice && variationPrice.wholesale) {
					const wholesalePrice = getWholesalePrice(variationPrice.wholesale, quantity, price);
					price = wholesalePrice;
				}
			}
			const totalPrice = price * quantity;
			return totalPrice;
		},
		getOrderVariationsTotalPrice() {
			const total = this.orderVariations.reduce((accumulator, orderVariation) => {
				const price = this.getOrderVariationPrice(orderVariation);
				return accumulator + price;
			}, 0);
			return total;
		},
		onOrderVariationInput(index, event) {
			const quantity = parseInt(event.target.value);
			const orderVariation = this.orderVariations[index];
			orderVariation.quantity = quantity;
			this.$set(this.orderVariations, index, orderVariation);
			// Update Order Variations
			const variations = duplicateVar(this.order.customer_data.variations) || [];
			const variationIndex = variations.findIndex(({ name }) => name === orderVariation.name);
			if (variationIndex !== -1) {
				variations[variationIndex].quantity = quantity;
			} else {
				const newVariationSelected = duplicateVar(orderVariation);
				delete newVariationSelected.cogs;
				delete newVariationSelected.stock;
				delete newVariationSelected.wholesale;
				variations.push(newVariationSelected);
			}
			this.$set(this.order.customer_data, 'variations', variations);
			// Set Others
			this.setWeight();
			this.calculateProductPrice();
			this.calculateCodCost();
			this.getShippingCosts();
		},
		getVariationLabel(variationName, attributes) {
			const names = variationName.split('||');
			const labels = names.map((name, index) => {
				const attribute = attributes[index].name;
				return `${attribute}: ${name}`;
			});
			const label = labels.join(', ');
			return label;
		},
		setOrderVariations() {
			const variationsPrices = this.getVariationsPrices();
			// const orderVariations = this.orderVariations;
			const orderVariations = this.order.customer_data.variations.slice();
			const attributes = this.getVariationsAttributes();
			const mapper = (variationPrice) => {
				const orderVariation = orderVariations.find(orderVariation => orderVariation.name === variationPrice.name);
				if (orderVariation) {
					variationPrice.quantity = parseInt(orderVariation.quantity);
					variationPrice.value = orderVariation.value;
					if (orderVariation.wholesale_price) {
						variationPrice.wholesale_price = orderVariation.wholesale_price;
					}
				} else {
					variationPrice.quantity = 0;
					const orderVariationLabel = this.getVariationLabel(variationPrice.name, attributes);
					variationPrice.value = orderVariationLabel;
				}
				return variationPrice;
			};
			const variations = variationsPrices.map(mapper);
			// this.order.customer_data.variations = variations;
			this.orderVariations = variations;
		},
		getSelectedOrderVariations() {
			const filter = orderVariation => parseInt(orderVariation.quantity) > 0;
			const filteredOrderVariations = this.orderVariations.filter(filter);
			const orderVariations = filteredOrderVariations.map((orderVariation) => {
				const wholesalePrice = getWholesalePrice(orderVariation.wholesale, orderVariation.quantity, orderVariation.price);
				if (orderVariation.price !== wholesalePrice) {
					orderVariation.wholesale_price = wholesalePrice;
				}
				delete orderVariation.wholesale;
				return orderVariation;
			});
			return orderVariations;
		},
		setOrderVariation() {
			const variationName = this.getVariationName();
			const variations = variationName.split('||');
			this.$set(this, 'orderVariation', variations);
		},
		onSelectOrderVariation(value, index) {
			this.$set(this.orderVariation, index, value);
		},
		getCodCost() {
			const shipping = this.order.shipping;
			const bump = this.order.bump;
			// const percentage = this.order.shipping.cod_percentage || 5;
			// const min = this.order.shipping.cod_min || 5000;
			// const max = this.order.shipping.cod_max || -1;
			const percentage = getPropValue(shipping, 'cod_percentage', 5);
			const min = getPropValue(shipping, 'cod_min', 5000);
			const max = getPropValue(shipping, 'cod_max', -1);
			const feeType = getPropValue(shipping, 'cod_fee_type', 'product');
			const productPrice = this.productPrice;
			const coupon = this.order.coupon;
			let totalPrice = productPrice;
			if (bump.checked) {
				totalPrice += bump.price;
			}
			if (coupon && coupon.code !== '' && coupon.discount_for === 'product') {
				totalPrice -= coupon.discount;
			}
			if (feeType === 'product_shipping') {
				let theShippingCost = duplicateVar(this.shippingCost);
				if (coupon && coupon.code !== '' && coupon.discount_for === 'shipping') {
					theShippingCost -= coupon.discount;
					if (theShippingCost < 0) {
						theShippingCost = 0;
					}
				}
				totalPrice += theShippingCost;
			}
			let codCost = totalPrice * (percentage / 100);
			if (codCost < min) {
				codCost = min;
			} else if (max !== -1 && codCost > max) {
				codCost = max;
			}
			return codCost;
		},
		calculateCodCost() {
			const codCost = this.getCodCost();
			this.order.shipping.cod_cost = codCost;
		},
		onBumpChange() {
			const weight = this.getWeight();
			this.productWeight = weight;
			this.getShippingCosts();
			if (this.order.shipping.cod) {
				this.calculateCodCost();
			}
		},
		async onPaymentChange() {
			const paymentMethod = this.order.payment.method;
			const isBankTransfer = paymentMethod === 'bank_transfer';
			const isCod = paymentMethod === 'cod';
			this.order.shipping.cod = isCod;
			let uniqueCodeValue = 0;
			let theUniqueCode = this.order.unique_code_price;
			if (this.product) {
				theUniqueCode = this.product.unique_code_price;
			}
			const uniqueCodeType = theUniqueCode.type;
			const min = theUniqueCode.min;
			const max = theUniqueCode.max;
			const productSlug = this.product ? this.product.slug : '';
			const hasUniqueCode = theUniqueCode.enabled;
			if (isBankTransfer) {
				// Get Unique Code
				if (hasUniqueCode) {
					uniqueCodeValue = this.uniqueCode > 0 ? duplicateVar(this.uniqueCode) : await this.generateUniqueCode(this.user.username, productSlug, min, max);
				}
				// Set COD Cost to 0
				this.order.shipping.cod_cost = 0;
			} else if (isCod) {
				this.calculateCodCost();
				uniqueCodeValue = 0;
			}

			this.order.unique_code_price.unique_code = uniqueCodeValue;
			this.order.unique_code_price.type = uniqueCodeType;

			// Re-Validate Coupon
			this.applyCoupon();
		},
		async generateUniqueCode(username, productSlug, min = 1, max = 1000) {
			return new Promise((res) => {
				if (productSlug !== '') {
					const params = {
						unique_code_min: min,
						unique_code_max: max,
					};
					const callback = (response) => {
						const code = response.data;
						const uniqueCode = code.unique_code;
						res(uniqueCode);
					};
					const errorCallback = () => {
						const generatedUniqueCode = Math.floor(Math.random() * max) + min;
						res(generatedUniqueCode);
					};
					productApi.getUniqueCode(username, productSlug, params, callback, errorCallback);
				} else {
					const callback = (response) => {
						const code = response.data;
						const uniqueCode = code.unique_code;
						res(uniqueCode);
					};
					const errorCallback = () => {
						const generatedUniqueCode = Math.floor(Math.random() * max) + min;
						res(generatedUniqueCode);
					};
					storefrontApi.getUniqueCode(callback, errorCallback);
				}
			});
		},
		onPhoneChange() {
			let phone = this.order.customer_data.phone;
			if (phone[0] === '0') {
				phone = phone.replace('0', '+62');
				this.order.customer_data.phone = phone;
			}
		},
		removeCartItem(cartItem) {
			const productId = cartItem.product_id.$oid;
			const variation = cartItem.variation;
			const index = this.cartItems.findIndex((cartItem) => {
				const isSameProduct = cartItem.product_id.$oid === productId;
				const isSameVariation = variation === cartItem.variation;
				return isSameProduct && isSameVariation;
			});
			if (index !== -1) {
				this.onCartRemove(index);
			}
		},
		onShippingChange(shippingCosts) {
			this.$set(this.order, 'shipping_costs', shippingCosts);
			let totalCost = 0;
			shippingCosts.forEach(({ cost }) => {
				totalCost += cost;
			});
			this.$set(this.order.shipping, 'cost', totalCost);
		},
		onRemoveShipping(originId) {
			const shippingCosts = duplicateVar(this.order.shipping_costs) || [];
			const index = shippingCosts.findIndex(({ origin }) => origin.id === originId);
			if (index !== -1) {
				shippingCosts.splice(index, 1);
				this.onShippingChange(shippingCosts);
				// this.$set(this.order, 'shipping_costs', shippingCosts);
			}
		},
		onCustomFieldUploaded(name, value) {
			if (this.customFieldsValues) {
				this.$set(this.customFieldsValues, name, value);
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			isOwner: 'isOwner',
			isUserCan: 'isUserCan',
			provinces: 'productCreator/provinces',
			cities: 'productCreator/cities',
			subdistricts: 'productCreator/subdistricts',
			isFetchingProvince: 'productCreator/isFetchingProvince',
			isFetchingCity: 'productCreator/isFetchingCity',
			isFetchingSubdistrict: 'productCreator/isFetchingSubdistrict',
		}),
		timezone() {
			return this.user ? this.user.timezone : 'Asia/Jakarta';
		},
		orderDate() {
			const createdAt = this.order.created_at;
			const date = this.moment.utc(createdAt).tz(this.timezone);
			const dateFormatted = date.format('dddd, DD-MM-YYYY');
			// return `${date.format('DD-MM-YYYY')} at ${date.format('HH:mm:ss')}`;
			return dateFormatted;
		},
		orderTime() {
			const date = this.moment.utc(this.order.created_at).tz(this.timezone);
			const dateFormatted = date.format('HH:mm:ss');
			return dateFormatted;
		},
		showAddressDetails() {
			const customerData = this.orderData.customer_data;
			const isShow = !!customerData
				&& !!(customerData.state || customerData.city || customerData.district || customerData.complete_district || customerData.subdistrict_id || customerData.city_id || customerData.province_id);
			return isShow;
		},
		uniqueCodeType() {
			// return this.orderData.unique_code_price && this.orderData.unique_code_price.type === 'decrease' ? '-' : '';
			const uniqueCodePrice = this.order && this.order.unique_code_price ? this.order.unique_code_price : this.orderData.unique_code_price;
			const typeSymbol = uniqueCodePrice && uniqueCodePrice.type === 'decrease' ? '-' : '';
			return typeSymbol;
		},
		isFetching() {
			return this.isFetchingShipping
				|| this.isFetchingProvince
				|| this.isFetchingCity
				|| this.isFetchingSubdistrict
				|| this.isValidatingCoupon
				|| this.shippingCostRequests.length > 0;
		},
		simpleProductPrice() {
			const quantity = this.productQuantity;
			const hasProductData = !!this.order.product_data;
			let normalPrice = hasProductData ? this.order.product_data.price : this.product.price;
			const salePrice = hasProductData ? this.order.product_data.sale_price : this.product.sale_price;
			if (salePrice && salePrice.enabled) {
				if (salePrice.schedule.enabled) {
					const dateFormat = 'DD-MM-YYYYY HH:mm:ss';
					const saleStart = salePrice.schedule.since;
					const saleEnd = salePrice.schedule.until;
					const saleStartDate = this.moment(saleStart, dateFormat);
					const saleEndDate = this.moment(saleEnd, dateFormat);
					const now = this.moment();
					const isSaleValid = now.isBetween(saleStartDate, saleEndDate);
					if (isSaleValid) {
						normalPrice = salePrice.price;
					}
				} else {
					normalPrice = salePrice.price;
				}
			}
			const wholesales = hasProductData ? this.order.product_data.wholesale : this.product.wholesale;
			const wholesalePrice = getWholesalePrice(wholesales, quantity, normalPrice);
			return wholesalePrice;
		},
		simpleProductWeight() {
			// return this.order.product_data ? this.order.product_data.weight : this.product.weight;
			return this.order.product_weight ? this.order.product_weight : this.product.weight;
		},
		productPrice() {
			return this.order.product_price;
		},
		productName() {
			return this.order.product_data ? this.order.product_data.name : this.product.name;
		},
		grossRevenue() {
			const uniqueCodePrice = this.order.unique_code_price;
			const uniqueCode = uniqueCodePrice.type === 'decrease'
				? uniqueCodePrice.unique_code * -1 : uniqueCodePrice.unique_code;
			const shippingCost = this.shippingCost;
			const codCost = this.codCost;
			const discount = this.order.coupon ? this.order.coupon.discount : 0;
			const bumpPrice = this.order.bump.checked ? this.order.bump.price : 0;
			const totalRevenue = this.productPrice + shippingCost + codCost + bumpPrice;
			const grossRevenue = (totalRevenue - discount) + uniqueCode + this.otherCost;
			return grossRevenue;
		},
		isCouponEmpty() {
			return !this.order.coupon || this.order.coupon.code === '';
		},
		isSimpleProduct() {
			return this.order.product_data ? this.order.product_data.type === 'simple' : this.product.type === 'simple';
		},
		isMultipleVariations() {
			return !!this.order.customer_data.variations;
		},
		otherCost: {
			get() {
				const cost = this.order.other_cost || 0;
				return cost;
			},
			set(cost) {
				this.$set(this.order, 'other_cost', cost);
			},
		},
		additionalShippingCost: {
			get() {
				const isHasMarkup = !!this.order.shipping.markup;
				const productDataShippingMarkup = this.product && this.product.shipping.markup ? this.product.shipping.markup : 0;
				const cost = isHasMarkup ? this.order.shipping.markup : productDataShippingMarkup;
				return cost;
			},
			set(markup) {
				this.order.shipping.markup = markup;
			},
		},
		shippingMarkup() {
			const shippingCost = duplicateVar(this.order.shipping.cost);
			let cost = this.additionalShippingCost;
			if (shippingCost + cost < 0) {
				cost = shippingCost * -1;
			}
			return cost;
		},
		shippingCost() {
			let cost = this.order.shipping.cost ? duplicateVar(this.order.shipping.cost) : 0;
			if (!this.isFlatShipping && !this.isFreeShipping) {
				cost += this.shippingMarkup;
			}
			return cost;
		},
		shippingCostOriginal() {
			const cost = this.order.shipping.original_cost ? duplicateVar(this.order.shipping.original_cost) : 0;
			return cost;
		},
		selectedOrderVariations() {
			const filter = orderVariation => parseInt(orderVariation.quantity) > 0;
			const filtered = this.orderVariations.filter(filter);
			return filtered;
		},
		isShowShipping() {
			return this.order && this.order.shipping.courier !== '' && this.order.shipping.cost !== 0;
		},
		isFlatShipping() {
			const isFlat = Object.prototype.hasOwnProperty.call(this.orderData.shipping, 'flat')
				? this.orderData.shipping.flat : this.product && this.product.shipping.flat_shipping;
			return isFlat;
		},
		isFreeShipping() {
			const isFree = this.product ? this.product.shipping.free_shipping : false;
			return isFree;
		},
		codCost() {
			const codCost = this.order.shipping.cod && this.order.shipping.cod_cost ? this.order.shipping.cod_cost : 0;
			return codCost;
		},
		isCod() {
			return this.order.shipping.cod;
		},
		isCodIncludeShipping() {
			return this.order.shipping.cod && this.order.shipping.cod_include_shipping;
		},
		selectedCourier() {
			return this.order && this.order.shipping ? this.order.shipping.courier : '';
		},
		isCodUnavailable() {
			const isSiCepatInvalid = this.selectedCourier === 'sicepat' && this.subdistrict && !this.subdistrict.scpt_cod;
			const isJneInvalid = this.selectedCourier === 'jne' && this.subdistrict && !this.subdistrict.jne_destination;
			const isSapInvalid = this.selectedCourier === 'sap' && this.subdistrict && !this.subdistrict.sap_cod;
			const isJntInvalid = (this.selectedCourier === 'jnt' || this.selectedCourier === 'J&T') && this.subdistrict && !this.subdistrict.jnt_destination;
			const isInvalid = this.isCod && (isSiCepatInvalid || isJneInvalid || isSapInvalid || isJntInvalid);
			return isInvalid;
		},
		codInfo() {
			let label = '';
			const cod = this.order.shipping.cod;
			if (cod) {
				const percentage = this.order.shipping.cod_percentage;
				const feeType = this.order.shipping.cod_fee_type;
				const feeTypeLabel = feeTypeLabels[feeType];
				const min = this.order.shipping.cod_min || 0;
				const max = this.order.shipping.cod_max || 0;
				const minLabel = formatMoney(min);
				const maxLabel = formatMoney(max);
				label = `${percentage}% x (${feeTypeLabel} - Discount)<br>Min: ${minLabel}, Max: ${maxLabel}`;
			}
			return label;
		},
		isStorefront() {
			return this.orderData.type && this.orderData.type === 'storefront';
		},
		totalCartWeight() {
			const cartItems = this.order.cart_items;
			const totalWeight = cartItems.reduce((prevWeight, { qty, weight }) => {
				const currentWeight = qty * weight;
				return prevWeight + currentWeight;
			}, 0);
			return totalWeight;
		},
		totalCartPrice() {
			const cartItems = this.order.cart_items || [];
			const totalPrice = cartItems.reduce((prevPrice, { price }) => prevPrice + price, 0);
			return totalPrice;
		},
		totalWeight() {
			return this.isStorefront ? this.totalCartWeight : this.productWeight;
		},
		isShowStoreShipping() {
			let isVisible = false;
			if (this.isStorefront) {
				isVisible = this.orderData.shipping_origin === 'store';
			} else {
				isVisible = !this.isFlatShipping;
			}
			return isVisible;
		},
		isShippingOriginStore() {
			return this.orderData.shipping_origin && this.orderData.shipping_origin === 'store';
		},
		isShippingOriginProduct() {
			return this.orderData.shipping_origin && this.orderData.shipping_origin === 'product';
		},
		cartItems: {
			get() {
				const cartItems = this.order && this.order.cart_items ? this.order.cart_items : [];
				return cartItems;
			},
			set(items) {
				if (this.order && this.order.cart_items) {
					this.$set(this.order, 'cart_items', items);
				}
			},
		},
		destinationId() {
			return this.orderData.customer_data ? parseInt(this.orderData.customer_data.subdistrict_id) : 0;
		},
		courierLabel() {
			return getCourierLabel(this.selectedCourier);
		},
		isDropship() {
			return this.orderData.dropship && this.orderData.dropship.enabled;
		},
		hasCustomFields() {
			return this.orderData.customer_data && this.orderData.customer_data.meta;
		},
		customFields() {
			const fields = this.formDetails ? this.formDetails.elements.fields : [];
			const customFields = fields.filter(({ name }) => name.includes('custom_'));
			return customFields;
		},
		isInstant() {
			const method = this?.orderData?.payment?.method || 'bank_transfer';
			const isInstant = method === 'instant';
			return isInstant;
		},
		customerParams() {
			// Customer Data
			const customerData = duplicateVar(this.order.customer_data);
			const params = {
				name: customerData.name,
				email: customerData.email,
				phone: customerData.phone,
				address: customerData.address,
				notes: customerData.notes,
			};
			if (customerData.meta) {
				const customerDataMeta = this.customFields.map((field) => {
					const value = this.customFieldsValues[field.name];
					const meta = {
						label: field.label,
						name: field.name,
						value,
					};
					return meta;
				});
				params.meta = customerDataMeta;
			}
			return params;
		},
		shippingMode() {
			return this?.order?.shipping?.mode ?? 'normal';
		},
		isSmartMode() {
			return this.shippingMode === 'smart';
		},
		isSmartCod() {
			return this.isCod && this.isSmartMode;
		},
		isCourierDisabled() {
			return this.couriers.length === 0 || this.isSmartCod;
		},
		isCourierServiceDisabled() {
			return this.shippingPackages.length === 0 || this.isSmartCod;
		},
		paymentMethod() {
			return this.orderData?.payment?.method ?? 'bank_transfer';
		},
		isShippingInvalid() {
			const invalid = !!this.courier && !this.shippingPackage;
			return invalid;
		},
		isEditInvalid() {
			return this.isFetching || (!this.isCouponEmpty && !this.isCouponValid) || this.isCodUnavailable || this.isShippingInvalid;
		},
	},
	watch: {
		provinces() {
			this.setProvince();
		},
		province(province) {
			if (province) {
				this.fetchCities(province.province_id);
			}
		},
		cities() {
			this.setCity();
		},
		city(city) {
			if (city) {
				this.fetchSubdistricts(city.city_id);
			}
		},
		subdistricts() {
			this.setSubdistrict();
		},
		subdistrict() {
			if (this.subdistrict && (this.product || this.isStorefront) && this.isShowStoreShipping) {
				this.getShippingCosts();
			}
			if (this.subdistrict && this.subdistrict.zip) {
				this.order.customer_data.zip = this.subdistrict.zip;
			}
		},
		couriers() {
			this.setCourier();
		},
		courier(courier) {
			if (courier) {
				this.shippingPackage = null;
				this.shippingPackages = [];
				const formatter = (cost) => {
					const shippingCost = cost.cost[0].value;
					const shippingCostFormatted = formatMoney(shippingCost);
					if (cost.service === 'CTC') {
						cost.service = 'REG';
					} else if (cost.service === 'CTCYES') {
						cost.service = 'YES';
					}
					const label = `${cost.service} (${shippingCostFormatted})`;
					cost.label = label;
					return cost;
				};
				const courierCode = courier.code;
				const courierCosts = courier.costs || [];
				const filteredCosts = courierCosts.filter(({ service }) => !(courierCode === 'sicepat' && service === 'SIUNT'));
				const costs = filteredCosts.map(formatter);
				this.shippingPackages = duplicateVar(costs);
			}
		},
		shippingPackages() {
			this.setShippingPackage();
		},
		shippingPackage(shippingPackage) {
			const oldShippingCost = duplicateVar(this.order.shipping.cost);
			if (!shippingPackage) {
				const oldGrossRevenue = duplicateVar(this.order.gross_revenue);
				const grossRevenue = oldGrossRevenue - oldShippingCost;
				this.order.shipping.cost = 0;
				this.order.gross_revenue = grossRevenue;
			} else {
				const cost = shippingPackage && shippingPackage.cost[0] ? shippingPackage.cost[0].value : 0;
				// const shippingCost = cost + this.additionalShippingCost;
				const shippingCost = cost;
				this.order.shipping.service = shippingPackage.service;
				this.order.shipping.cost = parseInt(shippingCost);
				this.order.shipping.original_cost = parseInt(shippingCost);
				if (cost !== oldShippingCost) {
					// Re-calculate COD Cost
					this.calculateCodCost();

					// Calculate Gross Revenue
					const oldGrossRevenue = duplicateVar(this.order.gross_revenue);
					const grossRevenue = (oldGrossRevenue - oldShippingCost) + shippingCost;
					this.order.gross_revenue = grossRevenue;

					// Re-validate Coupon
					if (!this.isCouponEmpty) {
						this.applyCoupon();
					}
				}
				// const cost = shippingPackage && shippingPackage.cost[0] ? shippingPackage.cost[0].value : 0;
				// this.order.shipping.service = shippingPackage.service;
				// this.order.shipping.cost = cost;
				// if (oldShippingCost !== cost && this.order.coupon.code !== '') {
				// 	this.applyCoupon();
				// }
			}
		},
		product() {
			// if (this.subdistrict && this.product) {
			if (this.subdistrict && this.product && !this.isInstant) {
				this.getShippingCosts();
			}
		},
		isShowAddress(value) {
			if (value) {
				this.initShippingData();
			}
		},
		orderVariation(orderVariation) {
			const variationName = orderVariation.join('||');
			const attributes = this.getVariationsAttributes();
			const label = this.getVariationLabel(variationName, attributes);
			this.order.customer_data.variation = label;

			// Re-Calculate Weight
			this.setWeight();

			if (this.product) {
				this.calculateProductPrice();
				this.calculateCodCost();
				this.applyCoupon();

				// Re-Fetch Shipping Cost
				this.getShippingCosts();
			}
		},
		productWeight() {
			// this.getShippingCosts();
		},
	},
};
</script>
